import { useState } from 'react';
import dayjs from 'dayjs';
import MoneyDisplay from '../../Common/MoneyDisplay';
import DebouncedNumberInput from '../../Common/DebouncedNumberInput';
import DebouncedInput from '../../Common/DebouncedInput';
import IntentsTable from '../../Common/IntentsTable';
import IntentDrawer from '../../Common/IntentDrawer';
import { PlusOutlined, DeleteOutlined, UpOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Space, Card, Table, DatePicker, Alert } from 'antd';

const ProjectBudgetIncomesPlan = ({
  projData,
  budgetPlanData
}) => {
  const {
    totalsData,
    incomePlanRows,
    isReadOnly,
    setIncomePlanRows,
    onStartEditing,
    loadBudgetExpensesPlanData,
  } = budgetPlanData;

  const [editedIntentId, setEditedIntentId] = useState(null);
  const [intentDrawerOpen, setIntentDrawerOpen] = useState(false);
  const handleRestructure = () => {
    const isEligible = (row) =>
      !row.intents?.some(i => i.is_paid || i.manually_overridden_fields?.includes('amount'));

    const fixedTotal = incomePlanRows
      .filter(row => !isEligible(row))
      .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

    const eligibleIndices = incomePlanRows
      .map((row, idx) => isEligible(row) ? idx : null)
      .filter(idx => idx !== null);

    const n = eligibleIndices.length;
    if (n === 0) return;

    const remaining = grandTotal - fixedTotal;
    const perRow = Math.round((remaining / n) * 100) / 100;
    const remainder = Math.round((remaining - perRow * n) * 100) / 100;

    setIncomePlanRows(incomePlanRows.map((row, idx) => {
      const pos = eligibleIndices.indexOf(idx);
      if (pos === -1) return row;
      const amount = pos === n - 1 ? perRow + remainder : perRow;
      return { ...row, amount };
    }));
  };

  const BASE_ROW = {
    id: null,
    title: '',
    date: null,
    amount: null,
  };

  const onAddRow = () => {
    setIncomePlanRows(prev => [...prev, { ...BASE_ROW }]);
  };

  const onDeleteRow = (rowInx) => {
    setIncomePlanRows(prev => prev.filter((_, idx) => idx !== rowInx));
  };

  const onMoveRow = (rowInx, direction) => {
    const newInx = rowInx + direction;
    if (newInx < 0 || newInx >= incomePlanRows.length) return;
    setIncomePlanRows(prev => {
      const updated = [...prev];
      [updated[rowInx], updated[newInx]] = [updated[newInx], updated[rowInx]];
      return updated;
    });
  };

  const onRowValueChange = (rowInx, key, value) => {
    setIncomePlanRows(prev =>
      prev.map((row, idx) =>
        idx !== rowInx ? row : { ...row, [key]: value }
      )
    );
  };

  const totalInstallments = incomePlanRows.reduce((acc, row) => acc + (parseFloat(row.amount) || 0), 0);
  const grandTotal = parseFloat(totalsData?.grand_total) || 0;
  const difference = grandTotal - totalInstallments;
  const isBalanced = Math.abs(difference) < 0.01;

  const generateInstallments = (months) => {
    if (grandTotal <= 0) return;
    const amountPerMonth = Math.round((grandTotal / months) * 100) / 100;
    const remainder = Math.round((grandTotal - amountPerMonth * months) * 100) / 100;

    const newRows = Array.from({ length: months }, (_, i) => ({
      id: null,
      title: `${projData.title} - Obrok #${i + 1}`,
      date: dayjs().add(i, 'month').format('YYYY-MM-DD'),
      amount: i === months - 1 ? amountPerMonth + remainder : amountPerMonth,
    }));
    setIncomePlanRows(newRows);
  };

  const columns = [
    {
      title: 'Obrok',
      dataIndex: 'title',
      width: '40%',
      render: (title, _, rowInx) =>
        isReadOnly ? (
          <b>{title}</b>
        ) : (
          <DebouncedInput
            size="small"
            placeholder="npr. Prvo plačilo, Končno plačilo..."
            value={title}
            onChange={val => onRowValueChange(rowInx, 'title', val)}
          />
        ),
    },
    {
      title: 'Datum',
      dataIndex: 'date',
      width: '25%',
      render: (date, _, rowInx) =>
        isReadOnly ? (
          date ? dayjs(date).format('DD/MM/YYYY') : '-'
        ) : (
          <DatePicker
            size="small"
            placeholder="Pričakovan datum"
            value={date ? dayjs(date) : null}
            onChange={(_, dateString) => onRowValueChange(rowInx, 'date', dateString || null)}
            style={{ width: '100%' }}
          />
        ),
    },
    {
      title: 'Znesek',
      dataIndex: 'amount',
      width: '20%',
      render: (amount, _, rowInx) =>
        isReadOnly ? (
          <MoneyDisplay size="small" type="INCOME" val={amount} />
        ) : (
          <DebouncedNumberInput
            size="small"
            value={amount}
            placeholder="Znesek"
            onChange={val => onRowValueChange(rowInx, 'amount', val)}
          />
        ),
    },
  ];

  if (!isReadOnly) {
    columns.push({
      title: '',
      dataIndex: null,
      width: '15%',
      render: (_, __, rowInx) => (
        <Space size="small">
          <Button
            size="small"
            disabled={rowInx === 0}
            onClick={() => onMoveRow(rowInx, -1)}
          >
            <UpOutlined />
          </Button>
          <Button
            size="small"
            disabled={rowInx === incomePlanRows.length - 1}
            onClick={() => onMoveRow(rowInx, 1)}
          >
            <DownOutlined />
          </Button>
          <Button
            size="small"
            danger
            onClick={() => onDeleteRow(rowInx)}
          >
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    });
  }

  return (
    <>
      <IntentDrawer
        open={intentDrawerOpen}
        onCancel={() => {
          setIntentDrawerOpen(false);
          setEditedIntentId(null);
        }}
        onDone={() => {
          if (loadBudgetExpensesPlanData) loadBudgetExpensesPlanData(null, false);
          setEditedIntentId(null);
          setIntentDrawerOpen(false);
        }}
        onDelete={() => {
          if (loadBudgetExpensesPlanData) loadBudgetExpensesPlanData(null, false);
          setEditedIntentId(null);
          setIntentDrawerOpen(false);
        }}
        editedIntentId={editedIntentId}
      />

      <Card
        title="Načrt prihodkov (obroki)"
        extra={isReadOnly && <Button onClick={onStartEditing}><EditOutlined /> Uredi</Button>}
      >
      <div style={{ marginBottom: 16 }}>
        <Space size="large">
          <div>
            <strong>Skupni proračun:</strong>{' '}
            <MoneyDisplay size="small" type="INCOME" val={grandTotal} />
          </div>
          <div>
            <strong>Skupaj obroki:</strong>{' '}
            <MoneyDisplay size="small" type="INCOME" val={totalInstallments} />
          </div>
          <div>
            <strong>Preostalo:</strong>{' '}
            <MoneyDisplay
              size="small"
              type={difference >= 0 ? 'INCOME' : 'EXPENSE'}
              val={Math.abs(difference)}
            />
            {difference < 0 && <span style={{ color: 'red' }}> (prekoračeno)</span>}
          </div>
        </Space>
      </div>

      {!isBalanced && incomePlanRows.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type={difference > 0 ? 'warning' : 'error'}
          message={
            difference > 0
              ? `Obrokom manjka ${difference.toFixed(2)} do skupnega zneska`
              : `Obroki presegajo skupni znesek za ${Math.abs(difference).toFixed(2)}`
          }
          action={
            <Button size="small" onClick={handleRestructure}>
              Prestrukturiraj
            </Button>
          }
        />
      )}

      <Table
        size="small"
        columns={columns}
        dataSource={incomePlanRows}
        pagination={false}
        rowKey={(row, index) => row.id ?? `new-${index}`}
        expandable={{
          expandedRowRender: (row) => (
            <IntentsTable
              intentsData={{ results: row.intents || [] }}
              onIntentEdit={(intent) => {
                setEditedIntentId(intent.id);
                setIntentDrawerOpen(true);
              }}
              setSelectedIds={() => {}}
            />
          ),
          rowExpandable: (row) => row.intents && row.intents.length > 0,
        }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 8,
          paddingRight: 8,
        }}
      >
        <strong>Skupaj:</strong>&nbsp;
        <MoneyDisplay size="small" type="INCOME" val={totalInstallments} />
      </div>

      {!isReadOnly && (
        <Space style={{ marginTop: 16 }}>
          <Button onClick={onAddRow}>
            <PlusOutlined /> Dodaj obrok
          </Button>
          <Button onClick={() => generateInstallments(1)} disabled={grandTotal <= 0}>
            1 mesec
          </Button>
          <Button onClick={() => generateInstallments(3)} disabled={grandTotal <= 0}>
            3 meseci
          </Button>
          <Button onClick={() => generateInstallments(6)} disabled={grandTotal <= 0}>
            6 mesecev
          </Button>
          <Button onClick={() => generateInstallments(12)} disabled={grandTotal <= 0}>
            12 mesecev
          </Button>
        </Space>
      )}
      </Card>
    </>
  );
};

export default ProjectBudgetIncomesPlan;
